import { getColumnValues } from '@analytical-alley/opt-wasm';
import { Table } from 'apache-arrow';

const normalizeDateObject = (value: Date) => {
    if (Number.isNaN(value.getTime())) {
        return undefined;
    }
    return value.toISOString().split('T')[0];
};

export const normalizePeriodDate = (value: unknown) => {
    if (value == null) {
        return undefined;
    }

    if (value instanceof Date) {
        return normalizeDateObject(value);
    }

    if (typeof value === 'bigint') {
        return normalizePeriodDate(Number(value / 1000n));
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        if (Math.abs(value) < 100_000) {
            return normalizeDateObject(new Date(value * 86_400_000));
        }
        const timestamp = Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
        return normalizeDateObject(new Date(timestamp));
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();

        if (!trimmed) {
            return undefined;
        }

        if (/^\d+$/.test(trimmed)) {
            return normalizePeriodDate(Number(trimmed));
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }

        const parsed = new Date(trimmed);
        const normalized = normalizeDateObject(parsed);

        if (normalized) {
            return normalized;
        }

        return trimmed;
    }

    return undefined;
};

export const toFiniteNumber = (value: unknown) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value === 'bigint') {
        return Number(value);
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
};

const getFirstAvailableColumnValues = <T>(table: Table, columnNames: string[]) => {
    for (const columnName of columnNames) {
        try {
            return getColumnValues<T>(table, columnName);
        } catch (_error) {
            //no-op
        }
    }

    return undefined;
};

export const extractGroupedCoefficientData = ({
    table,
    periodColumnNames,
}: {
    table: Table;
    periodColumnNames: string[];
}) => {
    const periodIndexes = getFirstAvailableColumnValues<unknown>(
        table,
        periodColumnNames,
    );
    const coefficients = getFirstAvailableColumnValues<unknown>(table, [
        'Coefficient',
    ]);
    const periodNames = getFirstAvailableColumnValues<unknown>(table, [
        'Period name',
    ]);

    if (!periodIndexes || !coefficients) {
        return {} as Record<
            number,
            { coefficient: number; count: number; minDate?: string }
        >;
    }

    const groupedData: Record<
        number,
        { coefficient: number; count: number; minDate?: string }
    > = {};

    const rowCount = Math.min(
        periodIndexes.length,
        coefficients.length,
        periodNames ? periodNames.length : Infinity,
    );

    for (let index = 0; index < rowCount; index++) {
        const periodIndex = toFiniteNumber(periodIndexes[index]);
        const coefficient = toFiniteNumber(coefficients[index]);
        const periodDate = periodNames
            ? normalizePeriodDate(periodNames[index])
            : undefined;

        if (periodIndex == null || coefficient == null) {
            continue;
        }

        if (!groupedData[periodIndex]) {
            groupedData[periodIndex] = {
                coefficient: 0,
                count: 0,
                minDate: periodDate ?? undefined,
            };
        }

        groupedData[periodIndex]!.coefficient += coefficient;
        groupedData[periodIndex]!.count += 1;
        if (
            periodDate &&
            (!groupedData[periodIndex]!.minDate ||
                periodDate < groupedData[periodIndex]!.minDate!)
        ) {
            groupedData[periodIndex]!.minDate = periodDate;
        }
    }

    return groupedData;
};
